import { useEffect, useState, type ReactNode } from "react";
import type { OrderDto } from "../api/types";
import { ORDER_STATUS_LABELS } from "./format";

export function StatusChip({ status }: { status: OrderDto["status"] }) {
  return <span className={`chip chip-${status}`}>{ORDER_STATUS_LABELS[status]}</span>;
}

export function BenchStamp({ bench }: { bench: string }) {
  return (
    <span className="bench-stamp" title={`Лавочка №${bench}`}>
      <small>Лавочка</small>
      <strong>{bench}</strong>
    </span>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className="toggle" title={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={event => onChange(event.target.checked)}
      />
      <span />
    </label>
  );
}

export function Modal({
  title,
  children,
  onClose,
  actions,
  size = "normal"
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  /**
   * Ширина окна. `wide` — для документов и техкарт: там таблица на шесть
   * колонок, и в узком окне она уезжает под горизонтальную прокрутку, где
   * кладовщик её и не находит.
   */
  size?: "normal" | "wide";
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={`modal ${size === "wide" ? "modal-wide" : ""}`}>
        <header>
          <h2>{title}</h2>
        </header>
        {children}
        {actions ? <div className="modal-actions">{actions}</div> : null}
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function LinkState({ connected }: { connected: boolean }) {
  return (
    <span className={`link-state ${connected ? "" : "offline"}`}>
      <i className="link-dot" />
      {connected ? "Онлайн" : "Переподключение"}
    </span>
  );
}

/**
 * Перерисовывает таймеры раз в секунду.
 *
 * Время в заказе не меняется, меняется только «сколько прошло», поэтому один
 * общий тик дешевле, чем таймер внутри каждой карточки.
 */
export function useTicker(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  return now;
}
